/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpParams } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, ReplaySubject } from 'rxjs';
import { BCode } from '@src/app/model/bcode';
import { Field } from '@src/app/shared/field/Field';
import { PreferredSupplier } from '@src/app/model/supplier';
import { Txn } from '@src/app/model/txn';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormUrlComponent } from '@src/app/shared/form/form-url/form-url.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { PreferredSupplierService } from '@src/app/modules/supply/preferred-supplier.service';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { uuid } from '@src/app/model/abstract-object';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { maxChars, minChars } from '@src/app/shared/validators';
import { Schedule } from '@src/app/model/schedule';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { PreferredSupplierPageNavRoute } from '@src/app/routes/preferred-supplier-page-nav';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';

@Component({
    selector: 'app-preferred-supplier-page',
    templateUrl: './preferred-supplier-page.component.html',
    styleUrls: ['./preferred-supplier-page.component.scss'],
    imports: [FormPageComponent]
})
export class PreferredSupplierPageComponent extends AbstractPageComponent {
    dataSvc = inject(PreferredSupplierService);
    dialogRef = inject<MatDialogRef<PreferredSupplierPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    validBCodes: BCode[] = [];
    schedules: Schedule[] = [];

    transactionsField = new GridField({
        field: {
            label: $localize`Transactions`, value: 'txnWithPayments',
            readonly: true, visible: Field.formOnly, formRow: 2, sendServer: false
        }, rowFactory: () => [
            FormNavLinkComponent.makeTxnLink('Ref'),
            FormTextComponent.make('Reference', 'reference', {readonly: true}),
            FormPicklistComponent.make('Type', 'typeId', 'type',
                { items: Txn.TYPES }, { cellOpts: { width: '12em' }, readonly: true }
            ),
            FormDateComponent.make('Date', 'txnDate', { cellOpts: { width: '8em' }, readonly: true }),
            FormNumberComponent.makeCurrency('Credit', 'credit', { readonly: true, numberOpts: {blankZero: true} }),
            FormNumberComponent.makeCurrency('Debit', 'debit', { readonly: true, numberOpts: {blankZero: true}  }),
            FormNumberComponent.makeCurrency('Outstanding', 'outstanding', { readonly: true, numberOpts: {blankZero: true}  }),
            FormButtonComponent.make('Approved', 'approvedAt', {
                type: 'icon', sendServer: false, label: 'Approved',
                cellOpts: { heading: '', width: '1%', style: 'height: 74px, width: 32px, padding: 0px 0px 0px 0px;' },
                calculateValue: (o: Txn) => o.approvedAt ? 'done' : ''
            }),
            FormDateComponent.make('From', 'svcFrom', {cellOpts: { width: '8em' }}),
            FormDateComponent.make('To', 'svcTo', { cellOpts: { width: '8em' } }),
            FormTextAreaComponent.make('Notes', 'notes', { cellOpts: { width: '50em' } }),
        ],
    });

    templateField = new GridField({
        field:
            { label: $localize`Template Transaction`, value: 'template', visible: Field.formOnly, formRow: 2, readonly: true },
        rowFactory: () => [
            FieldMaker.id(),
            FieldMaker.rev(),
            FormPicklistComponent.make('Account', 'bCodeId', null,{ items: this.validBCodes }),
            FormPicklistComponent.make('Schedule', 'scheduleId', null,
                { items: this.schedules }
            ),
            FieldMaker.notes({ cellOpts: { width: '50%' } }),
        ],
    });

    nameField = FormTextComponent.make('Name', 'name', {
        hint: 'The name of the company or sole trader who supplies you a service'
    });
    contactField = FormTextComponent.make('Contact Person', 'contactPerson', {
        hint: 'The name of the person you deal with in the company'
    });
    addressField = FormTextAreaComponent.make('Address', 'address', { formColumn: 1, visible: Field.formOnly  });
    phoneField = FormPhoneComponent.make('Telephone', 'phone', { formColumn: 2, hint: 'Your normal contact phone number' });
    emailField = FormEmailComponent.make('Email', 'email', { formColumn: 2, hint: 'Your normal contact email address' });
    urlField = FormUrlComponent.make('Web URL', 'web', { formColumn: 3,  });

    emergencyPhoneField = FormPhoneComponent.make('Emergency Number', 'emergencyPhone', {
        formColumn: 2, hint: 'Phone number to use in emergencies', visible: Field.formOnly
    });
    accountsEmailField = FormEmailComponent.make('Email Accounts', 'emailAccounts', {
        formColumn: 2, hint: 'Email address to use for accounts queries', visible: Field.formOnly
    });
    vatField = FormTextComponent.make('VAT', 'VAT', {
        formColumn: 3, validators: [maxChars(12), minChars(8)], visible: Field.formOnly,
        hint: 'If supplier is registered for VAT, you should enter their VAT number here. If not registered for VAT you still need their tax registration number here'
    });
    /*
    trnField = FormTextComponent.make('Tax Registration Number', 'taxRegistrationNr',
        {
            formColumn: 3, validators: [maxChars(12), minChars(8)], visible: Field.formOnly,
            hint: 'If a supplier is not registered for VAT, you still need to report them on form 46G, so you need their tax registration number. For sole traders, this is often their PPS number'
    });
    dunsField = FormTextComponent.make('DUNS', 'DUNS', { formColumn: 3, validators: [maxChars(9)],
        hint: 'A DUNS Number is a unique Dun & Bradstreet identifier that can be used to obtain additional company information, e.g. credit checks etc.',
        visible: Field.formOnly
    });
    */

    codeField = FormTextComponent.make('Code', 'code', {
        formColumn: 4,
        hint: 'A unique code used by te agent to identify a supplier.',
        visible: Field.formOnly
    });

    bicField = FormTextComponent.make('BIC', 'BIC', { formColumn: 4, validators: [maxChars(11), minChars(7)], visible: Field.formOnly });
    ibanField = FormTextComponent.make('IBAN', 'IBAN', { formColumn: 4, validators: [maxChars(34), minChars(16)], visible: Field.formOnly });
    bankField = FormTextComponent.make('Bank Account Name', 'bankAccountName', { formColumn: 4, validators: [maxChars(40)], visible: Field.formOnly });
    bankDetailField = FormTextAreaComponent.make('Bank Name & Address', 'bankNameAddress', { formColumn: 5, visible: Field.formOnly });

    configReady = new ReplaySubject<null>(1);

    sharedField = FormCheckboxComponent.make('Shared', 'isShared', {
        readonly: true, calculateValue: (o: PreferredSupplier) => o.omcTeamId ? false : true, formColumn: 3
    });
    omcTeamField = FormTextComponent.make('OMC', 'omcTeamId', { readonly: true, disable: true, formColumn: 3});

    config = new FormConfig({
        navRoute: PreferredSupplierPageNavRoute,
        docPath: 'omc-community/managers/suppliers',
        title: $localize`Preferred Supplier`,
        help: $localize`The list of preferred suppliers that the OMC does business with`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.nameField,
                this.sharedField,
                this.contactField,
                this.addressField,
                this.phoneField,
                this.emailField,
                this.emergencyPhoneField,
                this.accountsEmailField,
                this.omcTeamField,
                this.urlField,
                this.vatField,
                //this.trnField,
                //this.dunsField,
                this.codeField,
                this.bicField,
                this.ibanField,
                this.bankField,
                FieldMaker.notes({ formColumn: 5, visible: Field.formOnly }),
                this.bankDetailField,
            ],
            formLayout: [{ cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
            { cells: [{ colspan: 5, pageTab: 'yes' }] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        beforeEdit: this.beforeEdit.bind(this),
        objectFactory: this.objectFactory.bind(this),
        allowEdit: false,
        allowDelete: false,
        configReady: this.configReady,
    });

    constructor() {
        const scheduleSvc = inject(ScheduleService);
        const currentUserSvc = inject(CurrentUserService);
        const bCodeSvc = inject(BCodeService);

        super();

        currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed()).subscribe(user => {
            this.currentUser = user;
            this.config.fieldSet.fields.push(this.transactionsField);
            this.config.fieldSet.fields.push(this.templateField);
            bCodeSvc.get(false, new HttpParams().set('typeId', BCode.TYPE.EXPENSE.id + ',' + BCode.TYPE.ASSET.id)).subscribe(bcodes => {
                for (const c of bcodes) {
                    this.validBCodes.push(c as BCode);
                }
            });
            scheduleSvc.get(true).subscribe ( ss => {
                for (const s of ss) {
                    this.schedules.push (s as Schedule);
                }
            })
            this.configReady.next(null)
        })
    }

    objectFactory(): Observable<PreferredSupplier> {
        return of(new PreferredSupplier());
    }

    beforeEdit(o: PreferredSupplier): PreferredSupplier {

        this.config.readonly = o.omcTeamId ? false : true

        const payMap = new Map<uuid, Txn>();
        o.txnWithPayments = [];
        for (const txn of o.transactions) {
            if (txn.typeId === Txn.TYPE.BANK_OUT_ALLOC.id) {
                if (!payMap.has(txn.parentId)) {
                    const p = new Txn();
                    p.id = txn.parentId;
                    p.typeId = Txn.TYPE.BANK_OUT.id;
                    p.txnDate = txn.txnDate;
                    p.debit = txn.debit;
                    p.refNr = txn.parent.refNr;
                    payMap.set(txn.parentId, p);
                    o.txnWithPayments.push(p);
                } else {
                    payMap.get(txn.parentId).debit += txn.debit;
                }
            } else {
                if (txn.typeId !== Txn.TYPE.PURCHASE_CN_APPLY.id && txn.typeId !== Txn.TYPE.PURCHASE_CN_USED.id) {
                    o.txnWithPayments.push(txn);
                }
            }
        }
        o.txnWithPayments.sort((a, b) => b.txnDate.localeCompare(a.txnDate)); // Sort Most Recent First
        return o;
    }
}
